import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  CircularProgress,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Badge,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
// import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import UpdateIcon from '@mui/icons-material/Update';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import SecurityUpdateIcon from '@mui/icons-material/SecurityUpdate';

import { useDispatch } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router';
import { useGpuClasses } from '@/providers';

import {
  deleteRig,
  disableRemoteSupport,
  enableRemoteSupport,
  runAction,
  toggleMaintenance,
  updateRig,
  getNodeLatestVersion,
  upgradeNode,
} from '../../../../actions/rigs';
import NodeGridToolbar from './NodeGridToolbar';
import STATUSES from '../../../statuses';
import PollNode from './PollNode';
import RIG_SERVICES from './rigServices';
import RIG_TYPES from './rigTypes';
import PROVIDER_TYPES from '../../providerTypes';
import renderProgress from '../../../shared/renderProgress';
import BytesToMinimumGB from '../../../../util/BytesToMinimumGB';
import STORAGE_MODES from '../../RegionForm/Shared/storageModes';
import TelemetryInfo from './TelemetryInfo';
import Devices from './Devices';
import RemoveEphemeralNodeDialog from './RemoveEphemeralNodeDialog';

export default function Nodes({ region, rigs }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const gpu_types = useGpuClasses();
  const [computeSelectionModel, setComputeSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [ephemeralSelectionModel, setEphemeralSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [storageSelectionModel, setStorageSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [transitioningRigs, setTransitioningRigs] = useState([]);

  const [storageRigs, setStorageRigs] = useState([]);
  const [computeRigs, setComputeRigs] = useState([]);
  const [deviceRigs, setDeviceRigs] = useState([]);
  const [ephemeralRigs, setEphemeralRigs] = useState([]);
  const [telemetryInfoOpen, setTelemetryInfoOpen] = useState(false);

  const selectedComputeRigs = useMemo(() => {
    if (computeSelectionModel.type === 'include') {
      return Array.from(computeSelectionModel.ids);
    } else {
      // exclude mode: all rows except those in ids
      const excludedIds = Array.from(computeSelectionModel.ids);
      return computeRigs
        .map((rig) => rig.rig_uuid)
        .filter((id) => !excludedIds.includes(id));
    }
  }, [computeSelectionModel, computeRigs]);

  const selectedEphemeralRigs = useMemo(() => {
    if (ephemeralSelectionModel.type === 'include') {
      return Array.from(ephemeralSelectionModel.ids);
    } else {
      // exclude mode: all rows except those in ids
      const excludedIds = Array.from(ephemeralSelectionModel.ids);
      return ephemeralRigs
        .map((rig) => rig.rig_uuid)
        .filter((id) => !excludedIds.includes(id));
    }
  }, [ephemeralSelectionModel, ephemeralRigs]);

  const selectedStorageRigs = useMemo(() => {
    if (storageSelectionModel.type === 'include') {
      return Array.from(storageSelectionModel.ids);
    } else {
      // exclude mode: all rows except those in ids
      const excludedIds = Array.from(storageSelectionModel.ids);
      return storageRigs
        .map((rig) => rig.rig_uuid)
        .filter((id) => !excludedIds.includes(id));
    }
  }, [storageSelectionModel, storageRigs]);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [ephemeralRigToRemove, setEphemeralRigToRemove] = useState(null);
  const [upgradeRig, setUpgradeRig] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const upgrade_dialog_generation_ref = useRef(0);

  useEffect(() => {
    (async () => {
      // GpuClasses are fetched by Layout via context
    })();
  }, [dispatch, gpu_types]);

  useEffect(() => {
    const storage_rigs = [],
      compute_rigs = [],
      device_rigs = [],
      ephemeral_rigs = [];
    if (rigs !== null) {
      rigs.forEach((rig) => {
        if (rig.service === RIG_SERVICES.STORAGE) {
          storage_rigs.push(rig);
        } else {
          if (rig.type === RIG_TYPES.EPHEMERAL) {
            ephemeral_rigs.push(rig);
          } else if (rig.type === RIG_TYPES.DEVICE) {
            device_rigs.push(rig);
          } else {
            compute_rigs.push(rig);
          }
        }
      });
      setStorageRigs(storage_rigs);
      setComputeRigs(compute_rigs);
      setDeviceRigs(device_rigs);
      setEphemeralRigs(ephemeral_rigs);
    }
  }, [rigs]);

  //   const viewDetails = useCallback(
  //     (rig) => () => {
  //       history.push({
  //         pathname: `/resources/region/${region.region_uuid}/node/details/${rig.rig_uuid}`,
  //         search: `?provider_type=${region.provider_type}`,
  //       });
  //     },
  //     [history, region]
  //   );

  const updateNodeStatistics = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        runAction(
          region.provider_uuid,
          rig.region_uuid,
          rig.rig_uuid,
          'report',
        ),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const toggleNodeMaintenace = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        toggleMaintenance(region.provider_uuid, rig.region_uuid, rig.rig_uuid),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const enableNodeRemoteSupport = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        enableRemoteSupport(
          region.provider_uuid,
          rig.region_uuid,
          rig.rig_uuid,
        ),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const disableNodeRemoteSupport = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        disableRemoteSupport(
          region.provider_uuid,
          rig.region_uuid,
          rig.rig_uuid,
        ),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const editNode = useCallback(
    (rig) => () => {
      navigate(
        `/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/node/${rig.rig_uuid}/edit`,
      );
    },
    [navigate, region],
  );
  const removeNode = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        deleteRig(region.provider_uuid, rig.region_uuid, rig.rig_uuid),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const stopNode = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        updateRig(region.provider_uuid, rig.region_uuid, rig.rig_uuid, {
          command: 'stop',
        }),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const startNode = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        updateRig(region.provider_uuid, rig.region_uuid, rig.rig_uuid, {
          command: 'start',
        }),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const getRigName = useCallback((value, row) => {
    if (!row) {
      return '';
    }
    return row.friendly_name || '';
  }, []);

  const handleUpgradeDialogOpen = useCallback(
    (rig) => async () => {
      const request_generation = ++upgrade_dialog_generation_ref.current;
      setUpgradeRig(rig);
      setUpgradeDialogOpen(true);
      setLoadingVersion(true);
      setLatestVersion(null);
      try {
        const version = await dispatch(
          getNodeLatestVersion(region.provider_uuid, rig.region_uuid),
        );
        if (request_generation !== upgrade_dialog_generation_ref.current) {
          return;
        }
        setLatestVersion(version);
      } catch (error) {
        // Error is already handled by the action
      } finally {
        if (request_generation === upgrade_dialog_generation_ref.current) {
          setLoadingVersion(false);
        }
      }
    },
    [dispatch, region],
  );

  const handleUpgradeDialogClose = useCallback(() => {
    upgrade_dialog_generation_ref.current += 1;
    setUpgradeDialogOpen(false);
    setUpgradeRig(null);
    setLatestVersion(null);
    setLoadingVersion(false);
  }, []);

  const handleRemoveEphemeralNodeOpen = useCallback(
    (rig) => () => {
      setEphemeralRigToRemove(rig);
    },
    [],
  );

  const handleRemoveEphemeralNodeClose = useCallback(() => {
    setEphemeralRigToRemove(null);
  }, []);

  const handleRemoveEphemeralNodeConfirm = useCallback(() => {
    if (ephemeralRigToRemove) {
      removeNode(ephemeralRigToRemove)();
      setEphemeralRigToRemove(null);
    }
  }, [ephemeralRigToRemove, removeNode]);

  const handleUpgradeNode = useCallback(async () => {
    if (!upgradeRig) return;
    const rig_uuid = upgradeRig.rig_uuid;
    const region_uuid = upgradeRig.region_uuid;
    upgrade_dialog_generation_ref.current += 1;
    setTransitioningRigs((prevTransitioningRigs) => [
      ...prevTransitioningRigs,
      rig_uuid,
    ]);
    setUpgradeDialogOpen(false);
    setUpgradeRig(null);
    setLatestVersion(null);
    setLoadingVersion(false);
    await dispatch(
      upgradeNode(region.provider_uuid, region_uuid, rig_uuid),
    );
    setTransitioningRigs((prevTransitioningRigs) =>
      prevTransitioningRigs.filter(
        (transitioning_rig_uuid) => transitioning_rig_uuid !== rig_uuid,
      ),
    );
  }, [dispatch, region, upgradeRig]);

  const renderRigName = useCallback(
    (params) => {
      const nodeLink = (
        <Link
          component={RouterLink}
          to={`/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/node/${params.row.rig_uuid}/details`}
          sx={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Typography
            variant='body2'
            sx={{ '&:hover': { textDecoration: 'underline' } }}
          >
            {params.row.friendly_name}
          </Typography>
        </Link>
      );

      if (params.row.config?.remote_support_enabled) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={'Remote Support is Enabled'}>
              <span>
                <Stack direction='row' spacing={1} alignItems='center'>
                  {nodeLink}
                  <LockOpenOutlinedIcon color='error' />
                </Stack>
              </span>
            </Tooltip>
          </Box>
        );
      } else {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {nodeLink}
          </Box>
        );
      }
    },

    [region],
  );

  const getGpuType = useCallback(
    (value, row) => {
      if (!row) {
        return null;
      }
      if (gpu_types) {
        const gpu_type_id = row.resource_type_id || row.gpu_type_id;
        if (gpu_type_id) {
          const gpu_type = gpu_types.find(
            (gpu_type) => gpu_type.id === gpu_type_id,
          );
          if (gpu_type) {
            return gpu_type.name;
          }
        }
      }
      return null;
    },
    [gpu_types],
  );

  const getHotStorage = useCallback((value, row) => {
    if (!row) {
      return { used: 0, total: 0 };
    }
    if (row.state && row.state.storage) {
      const storage = row.state.storage;
      const hot_total = storage.hot_total;
      const hot_available = storage.hot_available;
      if (hot_total !== undefined && hot_available !== undefined) {
        return {
          used: BytesToMinimumGB(hot_total - hot_available) || 0,
          total: BytesToMinimumGB(hot_total) || 0,
        };
      }
    }
    return { used: 0, total: 0 };
  }, []);

  const getColdStorage = useCallback((value, row) => {
    if (!row) {
      return { used: 0, total: 0 };
    }
    if (row.state && row.state.storage) {
      const storage = row.state.storage;
      const cold_total = storage.cold_total;
      const cold_available = storage.cold_available;
      if (cold_total !== undefined && cold_available !== undefined) {
        return {
          used: BytesToMinimumGB(cold_total - cold_available) || 0,
          total: BytesToMinimumGB(cold_total) || 0,
        };
      }
    }
    return { used: 0, total: 0 };
  }, []);

  const renderStatus = useCallback(
    (params) => {
      if (transitioningRigs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }
      switch (params.row.status) {
        case STATUSES.ACTIVE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CheckCircleIcon color='success' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.OFFLINE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <StopCircleIcon />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.STOPPED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <StopCircleIcon />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.ERRORED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.MAINTENANCE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <WarningIcon color='warning' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        default:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CircularProgress size={20} />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
              <PollNode region={region} rig={params.row} interval={10000} />
            </Box>
          );
      }
    },
    [region, transitioningRigs],
  );

  const centralStorageComputeColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
        valueGetter: getRigName,
        renderCell: renderRigName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'gpu_type',
        headerName: 'Gpu Type',
        type: 'string',
        valueGetter: getGpuType,
        flex: 0.5,
      },
      {
        field: 'hot_storage',
        headerName: 'Storage',
        valueGetter: getHotStorage,
        renderCell: renderProgress,
        flex: 0.5,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];

          // Upgrade button (explicit, in main row)
          if (
            params.row.upgrade_available &&
            !transitioningRigs.includes(params.row.rig_uuid)
          ) {
            actions.push(
              <GridActionsCellItem
                icon={
                  <Badge color='brand' badgeContent={'!'}>
                    <SecurityUpdateIcon />
                  </Badge>
                }
                label='Upgrade'
                onClick={handleUpgradeDialogOpen(params.row)}
              />,
            );
          }

          if (params.row.config?.remote_support_enabled) {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Disable Support'
                onClick={disableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          } else {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Enable Support'
                onClick={enableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          }
          actions.push(
            //   <GridActionsCellItem
            //     icon={<InfoIcon />}
            //     label='View Details'
            //     onClick={viewDetails(params.row)}
            //     showInMenu
            //   />,
            <GridActionsCellItem
              icon={<EngineeringIcon />}
              label='Toggle Maintenance'
              onClick={toggleNodeMaintenace(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<UpdateIcon />}
              label='Update Stats'
              onClick={updateNodeStatistics(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<EditIcon />}
              label='Edit'
              onClick={editNode(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Remove'
              onClick={removeNode(params.row)}
              showInMenu
            />,
          );
          if (region.provider_type !== PROVIDER_TYPES.PHYSICAL.value) {
            actions.unshift(
              <GridActionsCellItem
                icon={<StopCircleOutlinedIcon />}
                label='Stop'
                onClick={stopNode(params.row)}
                showInMenu
                disabled={
                  ![STATUSES.ACTIVE, STATUSES.MAINTENANCE].includes(
                    params.row.status,
                  )
                }
              />,
            );
            actions.unshift(
              <GridActionsCellItem
                icon={<PlayCircleOutlineIcon />}
                label='Start'
                onClick={startNode(params.row)}
                showInMenu
                disabled={params.row.status !== STATUSES.STOPPED}
              />,
            );
          }
          return actions;
        },
      },
    ],
    [
      renderRigName,
      getRigName,
      getGpuType,
      renderStatus,
      //   viewDetails,
      toggleNodeMaintenace,
      updateNodeStatistics,
      editNode,
      removeNode,
      getHotStorage,
      startNode,
      stopNode,
      region.provider_type,
      disableNodeRemoteSupport,
      enableNodeRemoteSupport,
      handleUpgradeDialogOpen,
      transitioningRigs,
    ],
  );
  const localStorageComputeColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
        valueGetter: getRigName,
        renderCell: renderRigName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'gpu_type',
        headerName: 'Gpu Type',
        type: 'string',
        valueGetter: getGpuType,
        flex: 0.5,
      },
      {
        field: 'hot_storage',
        headerName: 'Hot Storage',
        valueGetter: getHotStorage,
        renderCell: renderProgress,
        flex: 0.5,
      },
      {
        field: 'cold_storage',
        headerName: 'Cold Storage',
        valueGetter: getColdStorage,
        renderCell: renderProgress,
        flex: 0.5,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];

          // Upgrade button (explicit, in main row)
          if (
            params.row.upgrade_available &&
            !transitioningRigs.includes(params.row.rig_uuid)
          ) {
            actions.push(
              <GridActionsCellItem
                icon={
                  <Badge color='brand' badgeContent={'!'}>
                    <SecurityUpdateIcon />
                  </Badge>
                }
                label='Upgrade'
                onClick={handleUpgradeDialogOpen(params.row)}
              />,
            );
          }

          if (params.row.config?.remote_support_enabled) {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Disable Support'
                onClick={disableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          } else {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Enable Support'
                onClick={enableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          }
          actions.push(
            //   <GridActionsCellItem
            //     icon={<InfoIcon />}
            //     label='View Details'
            //     onClick={viewDetails(params.row)}
            //     showInMenu
            //   />,
            <GridActionsCellItem
              icon={<EngineeringIcon />}
              label='Toggle Maintenance'
              onClick={toggleNodeMaintenace(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<UpdateIcon />}
              label='Update Stats'
              onClick={updateNodeStatistics(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<EditIcon />}
              label='Edit'
              onClick={editNode(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Remove'
              onClick={removeNode(params.row)}
              showInMenu
            />,
          );
          if (region.provider_type !== PROVIDER_TYPES.PHYSICAL.value) {
            actions.unshift(
              <GridActionsCellItem
                icon={<StopCircleOutlinedIcon />}
                label='Stop'
                onClick={stopNode(params.row)}
                showInMenu
                disabled={
                  ![STATUSES.ACTIVE, STATUSES.MAINTENANCE].includes(
                    params.row.status,
                  )
                }
              />,
            );
            actions.unshift(
              <GridActionsCellItem
                icon={<PlayCircleOutlineIcon />}
                label='Start'
                onClick={startNode(params.row)}
                showInMenu
                disabled={params.row.status !== STATUSES.STOPPED}
              />,
            );
          }
          return actions;
        },
      },
    ],
    [
      renderRigName,
      getRigName,
      getGpuType,
      renderStatus,
      //   viewDetails,
      toggleNodeMaintenace,
      updateNodeStatistics,
      editNode,
      removeNode,
      getHotStorage,
      getColdStorage,
      startNode,
      stopNode,
      region.provider_type,
      disableNodeRemoteSupport,
      enableNodeRemoteSupport,
      handleUpgradeDialogOpen,
      transitioningRigs,
    ],
  );

  const computeColumns =
    region.storage.mode === STORAGE_MODES.CENTRAL
      ? centralStorageComputeColumns
      : localStorageComputeColumns;

  const ephemeralColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
        valueGetter: getRigName,
        renderCell: renderRigName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'gpu_type',
        headerName: 'Gpu Type',
        type: 'string',
        valueGetter: getGpuType,
        flex: 0.5,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Remove'
            onClick={handleRemoveEphemeralNodeOpen(params.row)}
            showInMenu
            disabled={transitioningRigs.includes(params.row.rig_uuid)}
          />,
        ],
      },
    ],
    [
      renderRigName,
      getRigName,
      getGpuType,
      renderStatus,
      handleRemoveEphemeralNodeOpen,
      transitioningRigs,
    ],
  );

  const storageColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
        valueGetter: getRigName,
        renderCell: renderRigName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'hot_storage',
        headerName: 'Hot Storage',
        valueGetter: getHotStorage,
        renderCell: renderProgress,
        flex: 0.5,
      },
      {
        field: 'cold_storage',
        headerName: 'Cold Storage',
        valueGetter: getColdStorage,
        renderCell: renderProgress,
        flex: 0.5,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];

          // Upgrade button (explicit, in main row)
          if (
            params.row.upgrade_available &&
            !transitioningRigs.includes(params.row.rig_uuid)
          ) {
            actions.push(
              <GridActionsCellItem
                icon={
                  <Badge color='brand' badgeContent={'!'}>
                    <SecurityUpdateIcon />
                  </Badge>
                }
                label='Upgrade'
                onClick={handleUpgradeDialogOpen(params.row)}
              />,
            );
          }

          if (params.row.config?.remote_support_enabled) {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Disable Support'
                onClick={disableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          } else {
            actions.push(
              <GridActionsCellItem
                icon={<EngineeringIcon />}
                label='Enable Support'
                onClick={enableNodeRemoteSupport(params.row)}
                showInMenu
              />,
            );
          }
          actions.push(
            //   <GridActionsCellItem
            //     icon={<InfoIcon />}
            //     label='View Details'
            //     onClick={viewDetails(params.row)}
            //     showInMenu
            //   />,
            <GridActionsCellItem
              icon={<QueryStatsIcon />}
              label='Show Telemetry Info'
              onClick={() => setTelemetryInfoOpen(true)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<EngineeringIcon />}
              label='Toggle Maintenance'
              onClick={toggleNodeMaintenace(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<UpdateIcon />}
              label='Update Stats'
              onClick={updateNodeStatistics(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Remove'
              onClick={removeNode(params.row)}
              showInMenu
            />,
          );
          if (region.provider_type !== PROVIDER_TYPES.PHYSICAL.value) {
            actions.unshift(
              <GridActionsCellItem
                icon={<StopCircleOutlinedIcon />}
                label='Stop'
                onClick={stopNode(params.row)}
                showInMenu
                disabled={
                  ![STATUSES.ACTIVE, STATUSES.MAINTENANCE].includes(
                    params.row.status,
                  )
                }
              />,
            );
            actions.unshift(
              <GridActionsCellItem
                icon={<PlayCircleOutlineIcon />}
                label='Start'
                onClick={startNode(params.row)}
                showInMenu
                disabled={params.row.status !== STATUSES.STOPPED}
              />,
            );
          }
          return actions;
        },
      },
    ],
    [
      renderRigName,
      getRigName,
      renderStatus,
      toggleNodeMaintenace,
      updateNodeStatistics,
      removeNode,
      getHotStorage,
      getColdStorage,
      stopNode,
      startNode,
      region.provider_type,
      disableNodeRemoteSupport,
      enableNodeRemoteSupport,
      handleUpgradeDialogOpen,
      transitioningRigs,
      //   viewDetails,
    ],
  );

  const ComputeToolbar = () => (
    <NodeGridToolbar
      region={region}
      service={RIG_SERVICES.COMPUTE}
      type={RIG_TYPES.PERMANENT}
      selected={selectedComputeRigs}
    />
  );

  const EphemeralToolbar = () => (
    <NodeGridToolbar
      region={region}
      service={RIG_SERVICES.COMPUTE}
      type={RIG_TYPES.EPHEMERAL}
      selected={selectedEphemeralRigs}
    />
  );

  const StorageToolbar = () => (
    <NodeGridToolbar
      region={region}
      service={RIG_SERVICES.STORAGE}
      type={RIG_TYPES.PERMANENT}
      selected={selectedStorageRigs}
      disabled={
        storageRigs.length
          ? 'Only one storage node allowed per region'
          : undefined
      }
    />
  );

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <Typography variant='h6' paragraph gutterBottom>
            {region.storage.mode === STORAGE_MODES.LOCAL
              ? 'Nodes'
              : 'Compute Nodes'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  autoHeight
                  checkboxSelection
                  getRowId={(row) => row.rig_uuid}
                  columns={computeColumns}
                  rows={computeRigs}
                  rowSelectionModel={computeSelectionModel}
                  onRowSelectionModelChange={(newSelectionModel) => {
                    setComputeSelectionModel(newSelectionModel);
                  }}
                  slots={{
                    toolbar: ComputeToolbar,
                  }}
                  showToolbar
                  disableColumnSelector={false}
                  disableColumnFilter={false}
                />
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      {region.provider_type === PROVIDER_TYPES.PHYSICAL.value ? (
        <Devices region={region} rigs={deviceRigs} />
      ) : undefined}
      {ephemeralRigs.length ? (
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <Typography variant='h6' paragraph gutterBottom>
              Ephemeral Nodes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>
                  <DataGrid
                    autoHeight
                    checkboxSelection
                    getRowId={(row) => row.rig_uuid}
                    columns={ephemeralColumns}
                    rows={ephemeralRigs}
                    rowSelectionModel={ephemeralSelectionModel}
                    onRowSelectionModelChange={(newSelectionModel) => {
                      setEphemeralSelectionModel(newSelectionModel);
                    }}
                    slots={{
                      toolbar: EphemeralToolbar,
                    }}
                    showToolbar
                    disableColumnSelector={false}
                    disableColumnFilter={false}
                  />
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      ) : undefined}
      {region.storage.mode === STORAGE_MODES.CENTRAL ? (
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <Typography variant='h6' paragraph gutterBottom>
              Storage Nodes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>
                  <DataGrid
                    autoHeight
                    checkboxSelection
                    getRowId={(row) => row.rig_uuid}
                    columns={storageColumns}
                    rows={storageRigs}
                    rowSelectionModel={storageSelectionModel}
                    onRowSelectionModelChange={(newSelectionModel) => {
                      setStorageSelectionModel(newSelectionModel);
                    }}
                    slots={{
                      toolbar: StorageToolbar,
                    }}
                    showToolbar
                    disableColumnSelector={false}
                    disableColumnFilter={false}
                  />
                </div>
              </div>
            </div>
            <TelemetryInfo
              open={telemetryInfoOpen}
              handleClose={() => setTelemetryInfoOpen(false)}
              region={region}
            />
          </AccordionDetails>
        </Accordion>
      ) : undefined}
      <Dialog
        open={upgradeDialogOpen}
        onClose={handleUpgradeDialogClose}
        aria-labelledby='upgrade-dialog-title'
        aria-describedby='upgrade-dialog-description'
      >
        <DialogTitle id='upgrade-dialog-title'>Upgrade Node</DialogTitle>
        <DialogContent>
          <DialogContentText id='upgrade-dialog-description' sx={{ mb: 2 }}>
            <b>Current Version:</b> {upgradeRig?.rig_version || 'Unknown'}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            <b>Latest Version:</b>{' '}
            {loadingVersion ? (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            ) : (
              latestVersion || 'Unknown'
            )}
          </DialogContentText>
          {upgradeRig && !upgradeRig.maintenance_mode && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              The node must be in maintenance mode before upgrading. Please
              enable maintenance mode first.
            </Alert>
          )}
          <DialogContentText color='error' sx={{ mt: 2 }}>
            <b>Warning:</b> Upgrading a node will stop any running jobs during
            the upgrade process. The node may reboot until the upgrade is
            complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpgradeDialogClose} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handleUpgradeNode}
            color='brand'
            variant='contained'
            disabled={
              loadingVersion ||
              !upgradeRig ||
              !upgradeRig.maintenance_mode ||
              transitioningRigs.includes(upgradeRig.rig_uuid)
            }
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
      <RemoveEphemeralNodeDialog
        open={Boolean(ephemeralRigToRemove)}
        onClose={handleRemoveEphemeralNodeClose}
        onConfirm={handleRemoveEphemeralNodeConfirm}
      />
    </>
  );
}
